import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, Users, FileBarChart, Activity, CalendarClock, Clock, RefreshCw } from 'lucide-react';
import { Campaign, SubmissionData, User, Brand } from '../../../types';
import { CampaignStatus, SubmissionStatus, UserRole, Department, WorkflowStage, AdminView } from '../../../enums';
import { apiClient } from '../../../services/apiClient';
import { asArray } from '../../../utils/asArray';

interface DashboardViewProps {
    submissions: SubmissionData[];
    campaigns: Campaign[];
    onViewSite: () => void;
    currentUser: User;
    onNavigate: (view: AdminView) => void;
    brands: Brand[];
}

export const DashboardView: React.FC<DashboardViewProps> = ({ submissions, campaigns, onViewSite, currentUser, onNavigate, brands }) => {
  const [campaignList, setCampaignList] = useState<Campaign[]>(campaigns);
  const [extendTarget, setExtendTarget] = useState<Campaign | null>(null);
  const [newEndDate, setNewEndDate] = useState<string>('');
  const [savingExtension, setSavingExtension] = useState(false);
  const [extensionError, setExtensionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  const brandLookup = useMemo(() => new Map(brands.map((b) => [b.id, b.name])), [brands]);
  const resolveBrandName = (sub: SubmissionData) => {
    const explicit = (sub as any).brandName || (sub as any).brand?.name;
    if (explicit) return explicit;
    const fromList = asArray((sub as any).brands)
      .map((b: any) => (typeof b === 'string' ? brandLookup.get(b) || b : brandLookup.get(b?.id) || b?.name || b?.id))
      .find(Boolean);
    if (fromList) return fromList as string;
    const id = (sub as any).brandId;
    if (id) return brandLookup.get(id) || id;
    return 'Unknown brand';
  };

  useEffect(() => {
    setCampaignList(campaigns);
  }, [campaigns]);

  const startOfDay = (value?: string) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const today = startOfDay(new Date().toISOString());

  const handleOpenExtend = (campaign: Campaign) => {
    setExtendTarget(campaign);
    setExtensionError(null);
    const proposed = campaign.endDate || campaign.startDate || '';
    setNewEndDate(proposed);
  };

  const handleExtend = async () => {
    if (!extendTarget) return;
    setExtensionError(null);
    setSuccessMessage(null);
    const start = startOfDay(extendTarget.startDate);
    const end = startOfDay(newEndDate);
    if (!end) {
      setExtensionError('Choose a valid end date.');
      return;
    }
    if (start && end.getTime() < start.getTime()) {
      setExtensionError('New end date cannot be before start date.');
      return;
    }
    setSavingExtension(true);
    try {
      const payload: Campaign = {
        ...extendTarget,
        endDate: newEndDate,
        status: CampaignStatus.ACTIVE,
        discountType: extendTarget.discountType || 'Percentage',
      };
      const updated = await apiClient.campaigns.update(extendTarget.id, payload);
      if (Array.isArray(updated)) {
        setCampaignList(updated);
      }
      setExtendTarget(null);
      setSuccessMessage('Campaign dates updated.');
    } catch (error: any) {
      setExtensionError(error?.message || 'Failed to extend campaign.');
    } finally {
      setSavingExtension(false);
    }
  };
  
  // --- FILTER LOGIC ---
  const brandNameSet = useMemo(() => new Set(brandFilter.map((id) => (brandLookup.get(id) || '').toLowerCase()).filter(Boolean)), [brandFilter, brandLookup]);
  const applyBrandFilter = brandFilter.length > 0;

  const roleScopedSubmissions = useMemo(() => submissions.filter(sub => {
      if (sub.type === 'Proposal') return false;
      if (currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SEO) return true;
      
      // Department Users see items relevant to their stage or items they likely processed
      if (currentUser.department === Department.FB) {
          return sub.currentStage === WorkflowStage.F_AND_B || sub.status === SubmissionStatus.APPROVED;
      }
      if (currentUser.department === Department.FP) {
          return sub.currentStage === WorkflowStage.F_AND_P || (sub.workflowLogs?.some(l => l.stage === WorkflowStage.F_AND_P));
      }
      if (currentUser.department === Department.DS) {
          return sub.currentStage === WorkflowStage.DIGITAL_SALES || sub.status === SubmissionStatus.APPROVED || sub.status === SubmissionStatus.EXPORTED;
      }
      return false;
  }), [submissions, currentUser]);

  const filteredSubmissions = useMemo(() => {
    if (!applyBrandFilter) return roleScopedSubmissions;
    return roleScopedSubmissions.filter((sub) => {
      const brandsList = sub.brands || [];
      if (!brandsList.length) return false;
      return brandsList.some((b) => brandNameSet.has(String(b).toLowerCase()));
    });
  }, [applyBrandFilter, brandNameSet, roleScopedSubmissions]);

  const visibleCampaigns = useMemo(() => {
    if (!applyBrandFilter) return campaignList;
    return campaignList.filter((c) => {
      if ((c as any).scope === 'ALL') return true;
      const ids = (c as any).brandIds || [];
      if (!Array.isArray(ids) || ids.length === 0) return false;
      return ids.some((id: string) => brandFilter.includes(id));
    });
  }, [applyBrandFilter, campaignList, brandFilter]);

  const startingToday = useMemo(() => {
    return visibleCampaigns.filter(
      (c) =>
        c.status === CampaignStatus.PLANNED &&
        startOfDay(c.startDate)?.getTime() === today?.getTime()
    );
  }, [visibleCampaigns, today]);

  const expiringToday = useMemo(() => {
    return visibleCampaigns.filter(
      (c) =>
        c.status === CampaignStatus.ACTIVE &&
        startOfDay(c.endDate)?.getTime() === today?.getTime()
    );
  }, [visibleCampaigns, today]);

  const recentlyExpired = useMemo(() => {
    if (!today) return [];
    const cutoff = new Date(today);
    cutoff.setDate(cutoff.getDate() - 3);
    return visibleCampaigns.filter((c) => {
      const end = startOfDay(c.endDate);
      if (!end) return false;
      return end.getTime() < today.getTime() && end.getTime() >= cutoff.getTime();
    });
  }, [visibleCampaigns, today]);

  const activeCampaignsCount = visibleCampaigns.filter(c => c.status === CampaignStatus.ACTIVE).length;
  
  // Pending count based on role
  const pendingSubmissionsCount = filteredSubmissions.filter(s => {
      if (currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SEO) return s.status === SubmissionStatus.PENDING;
      
      // Department specific pending
      if (currentUser.department === Department.FB) return s.currentStage === WorkflowStage.F_AND_B && s.status === SubmissionStatus.PENDING;
      if (currentUser.department === Department.FP) return s.currentStage === WorkflowStage.F_AND_P && s.status === SubmissionStatus.PENDING;
      if (currentUser.department === Department.DS) return s.currentStage === WorkflowStage.DIGITAL_SALES && s.status === SubmissionStatus.PENDING;
      
      return false;
  }).length;

  const totalItemsCount = filteredSubmissions.reduce((acc, curr) => acc + curr.items.length, 0);

  return (
    <div className="space-y-8 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
                <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">Dashboard Overview</h2>
                <p className="text-zinc-500 text-sm">
                    Welcome, {currentUser.name} ({currentUser.department}). 
                    {currentUser.role === UserRole.DEPT_USER ? " Here are your tasks." : " Here's what's happening."}
                </p>
            </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-bold text-zinc-500 uppercase">Brand Filters</p>
              <p className="text-[11px] text-zinc-400">Apply scope to campaigns, stats, and activity.</p>
            </div>
            <button
              className="text-xs font-bold text-teal-600 hover:text-teal-700"
              onClick={() => setBrandFilter([])}
            >
              Clear
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {brands.map((b) => {
              const checked = brandFilter.includes(b.id);
              return (
                <button
                  key={b.id}
                  onClick={() => {
                    const next = checked ? brandFilter.filter((id) => id !== b.id) : [...brandFilter, b.id];
                    setBrandFilter(next);
                  }}
                  className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${checked ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-200 hover:text-teal-700'}`}
                >
                  {b.name}
                </button>
              );
            })}
            {brands.length === 0 && <span className="text-xs text-zinc-500">No brands available.</span>}
          </div>
        </div>

        {/* Campaign Activity */}
        <div className="bg-white rounded-3xl border border-zinc-100 shadow-sm p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <CalendarClock className="w-5 h-5 text-teal-500" />
                    <h3 className="font-bold text-zinc-800">Today's Campaign Activity</h3>
                </div>
                {successMessage && (
                    <span className="text-sm font-bold text-emerald-600">{successMessage}</span>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Starting Today */}
                <div className="bg-teal-50/60 border border-teal-100 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-bold text-teal-800 uppercase tracking-wide">Starting Today</h4>
                        <span className="text-xs font-bold text-teal-600 bg-white px-2 py-1 rounded-lg border border-teal-100">{startingToday.length}</span>
                    </div>
                    <div className="space-y-3">
                        {startingToday.length === 0 ? (
                            <p className="text-xs text-teal-700/70">No campaigns start today.</p>
                        ) : startingToday.map((c) => (
                            <div key={c.id} className="bg-white rounded-xl p-3 border border-teal-100 shadow-sm">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="text-sm font-bold text-zinc-900">{c.name}</p>
                                        <p className="text-[11px] text-zinc-500">Valid {c.startDate} → {c.endDate}</p>
                                    </div>
                                    <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-100">Planned</span>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {c.aggregators?.map((agg) => (
                                        <span key={agg} className="text-[10px] bg-teal-50 text-teal-700 px-2 py-1 rounded border border-teal-100 font-semibold">{agg}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Expiring Today */}
                <div className="bg-amber-50/70 border border-amber-100 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-bold text-amber-800 uppercase tracking-wide">Expiring Today</h4>
                        <span className="text-xs font-bold text-amber-700 bg-white px-2 py-1 rounded-lg border border-amber-100">{expiringToday.length}</span>
                    </div>
                    <div className="space-y-3">
                        {expiringToday.length === 0 ? (
                            <p className="text-xs text-amber-700/80">No campaigns expire today.</p>
                        ) : expiringToday.map((c) => (
                            <div key={c.id} className="bg-white rounded-xl p-3 border border-amber-100 shadow-sm">
                                <div className="flex justify-between items-start gap-2">
                                    <div>
                                        <p className="text-sm font-bold text-zinc-900">{c.name}</p>
                                        <p className="text-[11px] text-zinc-500">Valid {c.startDate} → {c.endDate}</p>
                                    </div>
                                    <button
                                      onClick={() => handleOpenExtend(c)}
                                      className="text-[11px] font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 px-2.5 py-1 rounded-lg border border-amber-200 transition-colors inline-flex items-center gap-1"
                                    >
                                      <RefreshCw className="w-3 h-3" /> Extend
                                    </button>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {c.aggregators?.map((agg) => (
                                        <span key={agg} className="text-[10px] bg-amber-50 text-amber-700 px-2 py-1 rounded border border-amber-100 font-semibold">{agg}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recently Expired */}
                <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-bold text-zinc-800 uppercase tracking-wide">Recently Expired (last 3 days)</h4>
                        <span className="text-xs font-bold text-zinc-600 bg-white px-2 py-1 rounded-lg border border-zinc-200">{recentlyExpired.length}</span>
                    </div>
                    <div className="space-y-3">
                        {recentlyExpired.length === 0 ? (
                            <p className="text-xs text-zinc-500">None in the last 3 days.</p>
                        ) : recentlyExpired.map((c) => (
                            <div key={c.id} className="bg-white rounded-xl p-3 border border-zinc-100 shadow-sm">
                                <div className="flex justify-between items-start gap-2">
                                    <div>
                                        <p className="text-sm font-bold text-zinc-900">{c.name}</p>
                                        <p className="text-[11px] text-zinc-500">Ended {c.endDate}</p>
                                    </div>
                                    <button
                                      onClick={() => handleOpenExtend(c)}
                                      className="text-[11px] font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded-lg border border-teal-100 transition-colors inline-flex items-center gap-1"
                                    >
                                      <RefreshCw className="w-3 h-3" /> Extend
                                    </button>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {c.aggregators?.map((agg) => (
                                        <span key={agg} className="text-[10px] bg-zinc-50 text-zinc-600 px-2 py-1 rounded border border-zinc-200 font-semibold">{agg}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Stat 1 - Campaigns */}
            <div 
                onClick={() => onNavigate(AdminView.CAMPAIGNS)}
                className="bg-white p-6 rounded-2xl border border-zinc-100 shadow-sm relative overflow-hidden group hover:shadow-md hover:border-teal-200 cursor-pointer transition-all"
            >
                <div className="absolute right-0 top-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <TrendingUp className="w-24 h-24 text-teal-600" />
                </div>
                <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-teal-50 text-teal-600 rounded-xl group-hover:bg-teal-100 transition-colors">
                        <TrendingUp className="w-6 h-6" />
                    </div>
                    <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider group-hover:text-teal-600 transition-colors">Active Campaigns</span>
                </div>
                <div className="flex items-baseline gap-2">
                    <h3 className="text-3xl font-extrabold text-zinc-900 group-hover:text-teal-700 transition-colors">{activeCampaignsCount}</h3>
                </div>
                <p className="text-zinc-500 text-sm mt-2">Discount campaigns currently live.</p>
            </div>

            {/* Stat 2 - Submissions */}
            <div 
                onClick={() => onNavigate(AdminView.SUBMISSIONS)}
                className="bg-white p-6 rounded-2xl border border-zinc-100 shadow-sm relative overflow-hidden group hover:shadow-md hover:border-blue-200 cursor-pointer transition-all"
            >
                 <div className="absolute right-0 top-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Users className="w-24 h-24 text-blue-600" />
                </div>
                <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-xl group-hover:bg-blue-100 transition-colors">
                        <Users className="w-6 h-6" />
                    </div>
                    <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider group-hover:text-blue-600 transition-colors">
                        {currentUser.role === UserRole.DEPT_USER ? 'Your Pending Tasks' : 'Total Pending'}
                    </span>
                </div>
                 <div className="flex items-baseline gap-2">
                    <h3 className="text-3xl font-extrabold text-zinc-900 group-hover:text-blue-700 transition-colors">{pendingSubmissionsCount}</h3>
                    {pendingSubmissionsCount > 0 && (
                        <span className="text-sm font-bold text-amber-500 flex items-center">
                            Requires Action
                        </span>
                    )}
                </div>
                <p className="text-zinc-500 text-sm mt-2">Submissions waiting in your queue.</p>
            </div>

            {/* Stat 3 - Table / Items */}
            <div 
                onClick={() => onNavigate(AdminView.TABLE)}
                className="bg-white p-6 rounded-2xl border border-zinc-100 shadow-sm relative overflow-hidden group hover:shadow-md hover:border-emerald-200 cursor-pointer transition-all"
            >
                 <div className="absolute right-0 top-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <FileBarChart className="w-24 h-24 text-emerald-600" />
                </div>
                <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl group-hover:bg-emerald-100 transition-colors">
                        <FileBarChart className="w-6 h-6" />
                    </div>
                     <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider group-hover:text-emerald-600 transition-colors">Total Items</span>
                </div>
                 <div className="flex items-baseline gap-2">
                    <h3 className="text-3xl font-extrabold text-zinc-900 group-hover:text-emerald-700 transition-colors">{totalItemsCount}</h3>
                </div>
                <p className="text-zinc-500 text-sm mt-2">Visible items in workflow.</p>
            </div>
        </div>

        {/* Quick Activity Section */}
        <div className="bg-white rounded-3xl border border-zinc-100 shadow-sm p-6">
            <h3 className="font-bold text-zinc-800 mb-6 flex items-center gap-2">
                <Activity className="w-5 h-5 text-zinc-400" />
                Recent System Activity
            </h3>
            <div className="space-y-4">
                {filteredSubmissions.length === 0 ? (
                    <p className="text-zinc-400 text-sm">No activity recorded yet.</p>
                ) : (
                    filteredSubmissions.slice(0, 5).map((sub, i) => (
                        <div key={i} className="flex items-start gap-4 pb-4 border-b border-zinc-50 last:border-0 last:pb-0">
                            <div className="w-2 h-2 mt-2 rounded-full bg-zinc-300"></div>
                            <div>
                                <p className="text-sm font-medium text-zinc-800">
                                  Submission {sub.submissionId}
                                  <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100 text-[11px] font-bold">
                                    {resolveBrandName(sub)}
                                  </span>
                                </p>
                                <p className="text-xs text-zinc-400 mt-1">
                                  {(sub.timestamp ? new Date(sub.timestamp).toLocaleString() : '-')} • {sub.items.length} Items • <span className="font-bold text-zinc-500">{sub.currentStage}</span>
                                </p>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>

        {/* Extend Modal */}
        {extendTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 border border-zinc-100">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h4 className="text-lg font-bold text-zinc-900">Extend Campaign</h4>
                        <p className="text-sm text-zinc-500">{extendTarget.name}</p>
                    </div>
                    <button onClick={() => setExtendTarget(null)} className="text-zinc-400 hover:text-zinc-700 text-sm font-bold">Close</button>
                </div>
                <div className="space-y-3">
                    <div className="flex items-center justify-between bg-zinc-50 p-3 rounded-xl border border-zinc-100">
                        <span className="text-xs font-bold text-zinc-500 uppercase">Current</span>
                        <span className="text-sm font-semibold text-zinc-800">{extendTarget.startDate} → {extendTarget.endDate}</span>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-zinc-500 uppercase">New End Date</label>
                        <input
                          type="date"
                          value={newEndDate}
                          onChange={(e) => setNewEndDate(e.target.value)}
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
                        />
                        {extensionError && <span className="text-xs text-red-500 font-semibold">{extensionError}</span>}
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={() => setExtendTarget(null)} className="px-4 py-2 text-zinc-600 font-bold rounded-xl hover:bg-zinc-50">Cancel</button>
                    <button
                      onClick={handleExtend}
                      disabled={savingExtension}
                      className={`px-4 py-2 rounded-xl font-bold text-white shadow-lg ${savingExtension ? 'bg-zinc-400 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'} flex items-center gap-2`}
                    >
                        {savingExtension && <Clock className="w-4 h-4 animate-spin" />}
                        {savingExtension ? 'Saving...' : 'Save Extension'}
                    </button>
                </div>
            </div>
          </div>
        )}
    </div>
  );
};
